import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Eye, EyeOff, KeyRound } from "lucide-react";

export default function AdminCredentials() {
  const [cur, setCur] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState({ current: false, next: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/credentials")
      .then((r) => { setCur(r.data); setNewUsername(r.data.username || ""); })
      .catch(() => setCur({ username: "", email: "" }));
  }, []);

  const save = async () => {
    if (!currentPassword) return toast.error("Enter your current password to confirm changes.");
    const usernameChanged = (newUsername || "").trim() && newUsername.trim() !== (cur?.username || "");
    if (newPassword) {
      if (newPassword.length < 8) return toast.error("New password must be at least 8 characters.");
      if (newPassword !== confirmPassword) return toast.error("New password and confirmation do not match.");
    }
    if (!usernameChanged && !newPassword) return toast.error("Enter a new Login ID or a new password to update.");
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (usernameChanged) payload.new_username = newUsername.trim();
      if (newPassword) payload.new_password = newPassword;
      const r = await api.put("/admin/credentials", payload);
      setCur({ username: r.data.username, email: r.data.email });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success("Admin credentials updated. Use them at your next login.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update credentials.");
    } finally {
      setSaving(false);
    }
  };

  if (!cur) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  return (
    <div className="bg-white rounded-2xl soft-shadow p-6" data-testid="admin-credentials-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-forest" />
        <h2 className="font-heading text-2xl text-forest tracking-tight">Admin Credentials</h2>
      </div>
      <p className="text-sm text-stone-500 mb-5">Change your Super Admin Login ID and password. Changes take effect immediately and persist without any redeploy.</p>

      <div className="space-y-4 max-w-md">
        <div>
          <Label className="text-sm">Current Login ID</Label>
          <Input value={cur.username || cur.email || ""} disabled className="rounded-xl bg-stone-100 mt-1" data-testid="cred-current-username" />
        </div>

        <div>
          <Label className="text-sm">New Login ID</Label>
          <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="Enter new Login ID" className="rounded-xl bg-sand mt-1" data-testid="cred-new-username" />
        </div>

        <div className="pt-2 border-t border-stone-100">
          <div className="flex items-center gap-2 text-forest mb-3 mt-3"><KeyRound className="w-4 h-4" /><span className="text-sm font-medium">Change Password</span></div>

          <Label className="text-sm">New Password <span className="text-stone-400">(min 8 chars, leave blank to keep current)</span></Label>
          <div className="relative mt-1">
            <Input type={show.next ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="rounded-xl bg-sand pr-10" data-testid="cred-new-password" />
            <button type="button" onClick={() => setShow((v) => ({ ...v, next: !v.next }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" data-testid="cred-toggle-new">{show.next ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>

          <Label className="text-sm mt-3 block">Confirm New Password</Label>
          <Input type={show.next ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" className="rounded-xl bg-sand mt-1" data-testid="cred-confirm-password" />
        </div>

        <div className="pt-2 border-t border-stone-100">
          <Label className="text-sm mt-3 block">Current Password <span className="text-red-500">*</span> <span className="text-stone-400">(required to confirm)</span></Label>
          <div className="relative mt-1">
            <Input type={show.current ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter your current password" className="rounded-xl bg-sand pr-10" data-testid="cred-current-password" />
            <button type="button" onClick={() => setShow((v) => ({ ...v, current: !v.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" data-testid="cred-toggle-current">{show.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="cred-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />} Update Credentials
        </Button>
      </div>
    </div>
  );
}
